import { TypedDocumentNode, gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, easings, useTransition } from 'react-spring';
import styled, { CSSProperties, keyframes } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL, LabelL } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/gql';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useFormatAnyPosition } from '@sorare/core/src/lib/players';

import BaseballCardProperties from '@sorare/marketplace/src/components/card/BaseballCardProperties';
import FootballCardProperties from '@sorare/marketplace/src/components/card/FootballCardProperties';
import NBACardProperties from '@sorare/marketplace/src/components/card/NBACardProperties';
import TokenStatsIcon from '@sorare/marketplace/src/components/token/TokenStatsIcon';

import { CardReveal_anyCard } from './__generated__/index.graphql';
import { useBackgroundY } from './useBackgroundY';

const rarityColors: { [key in Rarity]?: string } = {
  [Rarity.common]: 'rgba(var(--c-rgb-common), 0.75)',
  [Rarity.limited]: 'rgba(var(--c-rgb-limited), 0.75)',
  [Rarity.rare]: 'rgba(var(--c-rgb-rare), 0.75)',
  [Rarity.super_rare]: 'rgba(var(--c-rgb-superRare), 0.75)',
  [Rarity.unique]: 'rgba(var(--c-rgb-unique), 0.9)',
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const rotation = keyframes`
  from { transform: rotateY(90deg) }
  to { transform: rotateY(0deg) }
`;
const Root = styled(animated.div)`
  --y-offset: 0px;

  position: fixed;
  inset: 0;
  overflow: hidden;
  background: radial-gradient(transparent, var(--c-black) 70%),
    repeating-linear-gradient(
      to right,
      transparent,
      var(--c-nd-500) 1px,
      transparent 2px,
      transparent 60px
    ),
    repeating-linear-gradient(
      to bottom,
      transparent var(--y-offset),
      var(--c-nd-500) calc(var(--y-offset) + 1px),
      transparent calc(var(--y-offset) + 2px),
      transparent calc(var(--y-offset) + 60px)
    ),
    var(--c-black);

  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in-out forwards;
`;
const RelativeWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: 100%;
  height: 100%;
`;
const RevealElement = styled(animated.div)`
  position: absolute;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PositionText = styled.span`
  font-family: var(--rigid-square);
  font-size: 7vw;
  font-weight: bold;
  text-transform: uppercase;
  text-shadow:
    0 0 4px rgba(255, 255, 255, 1),
    0 0 6px rgba(255, 255, 255, 0.6),
    0 0 8px rgba(255, 255, 255, 0.2);
  max-width: 100%;
`;
const PictureShadow = styled(Horizontal).attrs({ center: true })`
  --shadow-color: var(--c-white);

  width: 100%;
  filter: drop-shadow(0 0 var(--double-unit) var(--shadow-color));
`;
const Picture = styled.img`
  width: 15%;
  min-width: 150px;
  max-width: 90%;
`;
const CardWrapper = styled(Vertical).attrs({ center: true, gap: 2 })`
  width: 80%;
`;
const FadeIn = styled(Vertical).attrs({ center: true })`
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-in-out forwards;
  animation-delay: 1s;
`;
const CardPicture = styled(Picture)`
  opacity: 0;
  animation:
    ${rotation} 0.5s ease-in-out forwards,
    ${fadeIn} 0.5s ease-in-out forwards;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

type Props = {
  card: CardReveal_anyCard;
  onAnimationEnd?: () => void;
};
export const CardReveal = ({ card, onAnimationEnd }: Props) => {
  const ended = useRef(false);
  const [i, setI] = useState(0);
  const formatPosition = useFormatAnyPosition();

  const steps = [
    <Vertical key={card.slug} center>
      {card.anyPositions.map(anyPosition => (
        <PositionText key={anyPosition}>
          {formatPosition(anyPosition)}
        </PositionText>
      ))}
    </Vertical>,
    card.anyPlayer.country.pictureUrl && (
      <PictureShadow>
        <Picture
          key={card.anyPlayer.country.slug}
          src={card.anyPlayer.country.pictureUrl}
          alt=""
        />
      </PictureShadow>
    ),
    card.anyPlayer.activeClub?.pictureUrl && (
      <PictureShadow>
        <Picture
          key={card.anyPlayer.activeClub.slug}
          src={card.anyPlayer.activeClub.pictureUrl}
          alt=""
        />
      </PictureShadow>
    ),
    card.pictureUrl && (
      <CardWrapper>
        <FadeIn gap={0}>
          <HeadlineXL brand>
            <FormattedMessage
              id="CardReveal.title"
              defaultMessage="New signing"
            />
          </HeadlineXL>
          <LabelL>
            <FormattedMessage
              id="CardReveal.subtitle"
              defaultMessage="Welcome {name} to your team"
              values={{ name: card.anyPlayer.displayName }}
            />
          </LabelL>
        </FadeIn>
        <PictureShadow
          style={
            {
              '--shadow-color': rarityColors[card.rarityTyped],
            } as CSSProperties
          }
        >
          <CardPicture key={card.slug} src={card.pictureUrl} alt="" />
        </PictureShadow>
        <FadeIn gap={1.5}>
          {isType(card, 'Card') && (
            <FootballCardProperties
              card={card}
              stats={<TokenStatsIcon card={card} />}
              withTransferMalus={false}
            />
          )}
          {isType(card, 'NBACard') && <NBACardProperties card={card} />}
          {isType(card, 'BaseballCard') && (
            <BaseballCardProperties card={card} />
          )}
          <Button
            size="medium"
            color="secondary"
            onClick={onAnimationEnd}
            fullWidth
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        </FadeIn>
      </CardWrapper>
    ),
  ].filter(Boolean);

  const transitions = useTransition(steps[i], {
    from: {
      opacity: -1,
      scale: 1.5,
      y: 1000,
    },
    enter: {
      opacity: 1,
      scale: 1,
      y: 0,
      config: {
        duration: 800,
        easing: easings.easeOutCirc,
      },
    },
    leave: {
      opacity: -1,
      scale: 1,
      y: -1000,
      delay: 200,
      config: {
        duration: 300,
        easing: easings.easeInCirc,
      },
    },
    exitBeforeEnter: true,
  });

  if (i === steps.length && !ended.current) {
    onAnimationEnd?.();
    ended.current = true;
  }

  return (
    <Root
      style={
        {
          '--y-offset': useBackgroundY({
            count: steps.slice(0, -1).length,
            onRest: () => setI(prev => prev + 1),
          }),
        } as CSSProperties
      }
    >
      <RelativeWrapper>
        {onAnimationEnd && (
          <CloseButtonWrapper>
            <CloseButton onClose={onAnimationEnd} />
          </CloseButtonWrapper>
        )}
        {transitions((style, node) => (
          <RevealElement style={style}>{node}</RevealElement>
        ))}
      </RelativeWrapper>
    </Root>
  );
};
CardReveal.fragments = {
  anyCard: gql`
    fragment CardReveal_anyCard on AnyCardInterface {
      slug
      pictureUrl
      anyPositions
      rarityTyped
      anyPlayer {
        slug
        displayName
        activeClub {
          slug
          pictureUrl
        }
        country {
          slug
          pictureUrl: flagUrl
        }
      }
      ...TokenStatsIcon_anyCard
      ...NBACardProperties_anyCard
      ...BaseballCardProperties_anyCard
      ...FootballCardProperties_card
    }
    ${FootballCardProperties.fragments.card}
    ${TokenStatsIcon.fragments.anyCard}
    ${NBACardProperties.fragments.anyCard}
    ${BaseballCardProperties.fragments.anyCard}
  ` as TypedDocumentNode<CardReveal_anyCard>,
};
